import React, { useEffect, useState } from 'react';
import { Search, FileText, FileSpreadsheet } from 'lucide-react';
import DefaultLayout from '../../../layout/DefaultLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import SelectField from '../../../components/Forms/Input/SelectField';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import ActionButton from '../../../components/Buttons/ActionButton';
import TipReportService from '../../../services/report/TipReportService';
import { useBranch } from '../../../hooks/branches/useBranch';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import { formatCurrency } from '../../../utils/formatCurrency';

interface ResultMonth {
  yearMonth: string;
  totalTip: number;
  salesCount: number;
}

const today = () => new Date().toISOString().slice(0, 10);
const firstDayOfYear = () => {
  const date = new Date();
  return new Date(date.getFullYear(), 0, 1).toISOString().slice(0, 10);
};

const TipReportPage: React.FC = () => {
  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { handle } = useApiErrorHandler();

  const [branchId, setBranchId] = useState<string | number>('');
  const [dateFrom, setDateFrom] = useState(firstDayOfYear());
  const [dateTo, setDateTo] = useState(today());

  const [result, setResult] = useState<{
    months: ResultMonth[];
    grandTotal: number;
  } | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  useEffect(() => {
    fetchBranchDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    if (!dateFrom || !dateTo) return;
    try {
      const data = await TipReportService.search({
        branchId: branchId ? Number(branchId) : undefined,
        dateFrom,
        dateTo,
      });
      setResult(data);
      setHasSearched(true);
    } catch (error: any) {
      handle(error);
    }
  };

  const currentFilter = () => ({
    branchId: branchId ? Number(branchId) : undefined,
    dateFrom,
    dateTo,
  });

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    try {
      await TipReportService.exportPdf(currentFilter());
    } catch (error: any) {
      handle(error);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    try {
      await TipReportService.exportExcel(currentFilter());
    } catch (error: any) {
      handle(error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <div className="mb-5 rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <SelectField
            label="Sucursal"
            name="branchId"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            options={[
              { value: '', label: 'Todas' },
              ...branchDropdown.map((b: any) => ({ value: b.id, label: b.name })),
            ]}
            iconKey="branch"
          />

          <DatePicker
            label="Desde"
            name="dateFrom"
            value={dateFrom}
            onChange={(value) => setDateFrom(value)}
            iconKey="calendar"
            withMargin={false}
          />

          <DatePicker
            label="Hasta"
            name="dateTo"
            value={dateTo}
            onChange={(value) => setDateTo(value)}
            iconKey="calendar"
            withMargin={false}
          />

          <div>
            <label className="mb-2.5 block select-none text-sm font-medium text-transparent">
              Acción
            </label>
            <ActionButton
              label="Consultar"
              icon={<Search className="h-4 w-4" />}
              disabled={!dateFrom || !dateTo}
              className="h-[50px]"
              onClick={handleSearch}
            />
          </div>
        </div>
      </div>

      {hasSearched && result && (
        <div className="rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-black dark:text-white">
              Propina por Mes — {dateFrom} a {dateTo}
            </h3>

            <div className="flex gap-2">
              <ActionButton
                label="Exportar a PDF"
                icon={<FileText className="h-4 w-4" />}
                variant="danger"
                size="sm"
                fullWidth={false}
                loading={isExportingPdf}
                loadingLabel="Exportando..."
                disabled={result.months.length === 0}
                onClick={handleExportPdf}
              />
              <ActionButton
                label="Exportar a Excel"
                icon={<FileSpreadsheet className="h-4 w-4" />}
                variant="success"
                size="sm"
                fullWidth={false}
                loading={isExportingExcel}
                loadingLabel="Exportando..."
                disabled={result.months.length === 0}
                onClick={handleExportExcel}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-2 text-left dark:bg-meta-4">
                  <th className="px-4 py-2 font-medium">Mes</th>
                  <th className="px-4 py-2 font-medium text-right">Ventas con Propina</th>
                  <th className="px-4 py-2 font-medium text-right">Propina Total</th>
                </tr>
              </thead>
              <tbody>
                {result.months.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-4 text-center text-body">
                      No hay ventas con propina en el rango seleccionado.
                    </td>
                  </tr>
                )}
                {result.months.map((month) => (
                  <tr key={month.yearMonth} className="border-t border-stroke dark:border-strokedark">
                    <td className="px-4 py-2">{month.yearMonth}</td>
                    <td className="px-4 py-2 text-right">{month.salesCount}</td>
                    <td className="px-4 py-2 text-right">${formatCurrency(month.totalTip)}</td>
                  </tr>
                ))}
              </tbody>
              {result.months.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-stroke font-semibold dark:border-strokedark">
                    <td className="px-4 py-2" colSpan={2}>
                      Total
                    </td>
                    <td className="px-4 py-2 text-right">
                      ${formatCurrency(result.grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </DefaultLayout>
  );
};

export default TipReportPage;
